import React from 'react';
import Div from '../../Element/Div/Div';
import Flex, { FlexProps } from '../../Flex/Flex';
import PageNumber from '../PageNumber/PageNumber';
import { CreateProps } from '../../../types/utils/CreateProps';
import Element from '../../Element/Element';
import Icon from '../../Icon/Icon';

type PageSelectorProps = CreateProps<
  {
    /** Goes selected page */
    goToPageNumber: (page: number) => void;
    /** Goes next page */
    goToNextPage: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
    /** Goes previous page */
    goToPreviousPage: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
    /** Visibility of left arrow page number */
    leftArrowPageNumber?: number;
    /** Visibility of right arrow page number */
    rightArrowPageNumber?: number;
    /** Visibility of first page */
    firstPage?: number;
    /** Visibility of last page */
    lastPage?: number;
    /** Visibility of left ellipsis */
    showLeftEllipsis?: boolean;
    /** Visibility of right ellipsis */
    showRightEllipsis?: boolean;
    /** Current item and its neightbours */
    currentPageAndAdjacentPages?: {
      pageNumber?: number;
      clickable?: boolean;
    }[];
  },
  FlexProps<'ul'>,
  'alignBaseline' | 'color'
>;

const PageSelector: React.FunctionComponent<PageSelectorProps> = ({
  goToPageNumber,
  goToNextPage,
  goToPreviousPage,
  leftArrowPageNumber,
  firstPage,
  showLeftEllipsis,
  currentPageAndAdjacentPages = [],
  showRightEllipsis,
  lastPage,
  rightArrowPageNumber,
  tagName = 'nav',
  ...props
}) => {
  return (
    <Element tagName={tagName as keyof JSX.IntrinsicElements} flexItem>
      <Flex
        tagName="ul"
        role="list"
        style={{ listStyle: 'none' }}
        centeredVertically
        flexItem
        color="purple500"
        {...props}
      >
        {leftArrowPageNumber && (
          <Element tagName="li" key="left-arrow-page-number">
            <Icon
              clickableAppearance
              style={{
                backgroundColor: 'transparent',
                verticalAlign: 'middle',
              }}
              onClick={goToPreviousPage as any} //Our icon supports tagName, however onClick doesn't mutate with it
              size="sm"
              tagName="button"
              name="chevron-left"
              color="purple500"
              hoverColor="black"
            />
          </Element>
        )}

        {firstPage && (
          <Element tagName="li">
            <PageNumber
              id="first-page-button"
              number={firstPage}
              active={!!firstPage}
              onClick={goToPageNumber}
            />
          </Element>
        )}

        {showLeftEllipsis && (
          <Element tagName="li" key="left-ellipsis">
            <Div
              aria-hidden="true"
              padding={{ horizontal: 'sm' }}
              color="black"
            >
              ...
            </Div>
          </Element>
        )}

        {currentPageAndAdjacentPages.map(({ pageNumber, clickable }, key) => (
          <Element tagName="li" key={key}>
            <PageNumber
              number={pageNumber as number}
              active={clickable}
              id={`page-${pageNumber}-button`}
              onClick={goToPageNumber}
            />
          </Element>
        ))}

        {showRightEllipsis && <Div padding={{ horizontal: 'sm' }}>...</Div>}

        {lastPage && (
          <Element tagName="li">
            <PageNumber
              id="last-page-button"
              number={lastPage}
              active={!!lastPage}
              onClick={goToPageNumber}
            />
          </Element>
        )}

        {rightArrowPageNumber && (
          <Element tagName="li" key="right-arrow-page-number">
            <Icon
              clickableAppearance
              style={{ backgroundColor: 'white', verticalAlign: 'middle' }}
              onClick={goToNextPage as any} //Our icon supports tagName, however onClick doesn't mutate with it
              size="sm"
              tagName="button"
              name="chevron-right"
              color="purple500"
              hoverColor="black"
            />
          </Element>
        )}
      </Flex>
    </Element>
  );
};

export default PageSelector;
